import { parseUTCTimestamp } from '@/lib/dateFormat'
import type { Conversation } from '@/types/conversation'

export interface SelectionChase {
  /** Selection the loaded pages are being aligned to */
  selectedId: string | undefined
  /** Filter those pages belong to */
  filterKey: string
}

/**
 * A direct link keeps loading until its row appears. A later filter change leaves
 * the open conversation in place, because walking later pages would scan a result
 * set that selection may not belong to.
 */
export function nextSelectionChase(
  chase: SelectionChase,
  selectedId: string | undefined,
  filterKey: string
): { chase: SelectionChase; follow: boolean } {
  if (chase.selectedId !== selectedId) {
    return { chase: { selectedId, filterKey }, follow: true }
  }
  return { chase, follow: chase.filterKey === filterKey }
}

/**
 * A direct link can point past the loaded pages. The list is sorted by `updated_at`
 * descending, so once the last loaded row is older than the selection, the selection
 * is not in the list (it is filtered out) and loading more would not find it.
 */
export function needsMorePagesForSelection(
  conversations: Pick<Conversation, 'id' | 'updated_at'>[],
  selected: Pick<Conversation, 'id' | 'updated_at'>
): boolean {
  if (conversations.some((item) => String(item.id) === String(selected.id))) return false
  const last = conversations.at(-1)
  if (!last) return false
  return (
    parseUTCTimestamp(last.updated_at).getTime() >= parseUTCTimestamp(selected.updated_at).getTime()
  )
}
